/* eslint-disable no-param-reassign */
import { Cell } from './game.slice';

export const createCell = ({
  colId,
  rowId,
  value,
}: {
  colId: Cell['colId'];
  rowId: Cell['rowId'];
  value: Cell['value'];
}): Cell => ({
  rowId,
  colId,
  visible: false,
  value,
});

const randomInt = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const generateBoard = (noOfRows: number, noOfCols: number) => {
  const board: Cell[][] = [];
  for (let row = 0; row < noOfRows; row += 1) {
    const boardRow: Cell[] = [];
    for (let col = 0; col < noOfCols; col += 1) {
      boardRow.push(
        createCell({
          colId: col,
          rowId: row,
          value: '',
        }),
      );
    }
    board.push(boardRow);
  }
  return board;
};

const placeBombs = (noOfBombs: number, board: Cell[][]) => {
  const findPlaceForBomb = (): [number, number] => {
    const row = randomInt(0, board.length - 1);
    const col = randomInt(0, board[0].length - 1);
    return [row, col];
  };

  for (let bombIndex = 0; bombIndex < noOfBombs; bombIndex += 1) {
    let bombRow;
    let bombCol;

    do {
      [bombRow, bombCol] = findPlaceForBomb();
    } while (board[bombRow][bombCol].value === 'X');

    board[bombRow][bombCol] = createCell({
      colId: bombCol,
      rowId: bombRow,
      value: 'X',
    });
  }
};

const isOutOfBound = (board: Cell[][], row: number, col: number) => {
  return row < 0 || row >= board.length || col < 0 || col >= board[0].length;
};

const noOfBombAtPlace = (board: Cell[][], x: number, y: number) => {
  if (isOutOfBound(board, x, y)) {
    return 0;
  }
  return board[x][y].value === 'X' ? 1 : 0;
};

const getCell = (board: Cell[][], row: number, col: number) => {
  if (isOutOfBound(board, row, col)) {
    return null;
  }
  return board[row][col];
};

const getNeighbourCells = (
  board: Cell[][],
  { colId: col, rowId: row }: Cell,
): Cell[] => {
  return [
    getCell(board, row - 1, col - 1),
    getCell(board, row - 1, col),
    getCell(board, row - 1, col + 1),
    getCell(board, row, col - 1),
    getCell(board, row, col + 1),
    getCell(board, row + 1, col - 1),
    getCell(board, row + 1, col + 1),
    getCell(board, row + 1, col),
  ].filter(Boolean) as Cell[];
};

const getNeighbourBombs = (board: Cell[][], row: number, col: number) => {
  const cells = getNeighbourCells(board, board[row][col]);
  return cells.reduce(
    (acc, cell) => acc + noOfBombAtPlace(board, cell.rowId, cell.colId),
    0,
  );
};

export const generateBoardWithMines = (
  noOfBombs: number,
  noOfRows: number,
  noOfCols: number,
): Cell[][] => {
  const board = generateBoard(noOfRows, noOfCols);

  placeBombs(noOfBombs, board);

  for (let i = 0; i < noOfRows; i += 1) {
    for (let j = 0; j < noOfCols; j += 1) {
      if (noOfBombAtPlace(board, i, j) === 0) {
        const neighbourBombs = getNeighbourBombs(board, i, j);
        if (neighbourBombs > 0) {
          board[i][j].value = neighbourBombs;
        }
      }
    }
  }

  return board;
};

export const dsf = (board: Cell[][], cell: Cell) => {
  const cellsToProcess = new Map<number, Map<number, 'ok'>>();
  const visitedCellMap = new Map<number, Map<number, 'ok'>>();

  const addToMap = (
    map: typeof cellsToProcess | typeof visitedCellMap,
    { rowId, colId }: Cell,
  ) => {
    if (!map.has(rowId)) {
      map.set(rowId, new Map());
    }
    const rowmap = map.get(rowId);
    if (rowmap) {
      rowmap.set(colId, 'ok');
    }
  };

  const isCellVisited = ({ rowId, colId }: Cell) => {
    if (!visitedCellMap.has(rowId)) {
      return false;
    }
    const colMap = visitedCellMap.get(rowId);
    return !!colMap && colMap.has(colId);
  };

  const processNeighbouringCells = (processingCell: Cell) => {
    const neighbourCells = getNeighbourCells(board, processingCell);
    neighbourCells.forEach(neighbourCell => {
      if (!isCellVisited(neighbourCell)) {
        addToMap(cellsToProcess, neighbourCell);
      }
    });
  };

  const processCell = (processingCell: Cell) => {
    addToMap(visitedCellMap, processingCell);
    processingCell.visible = true;
    if (processingCell.value === '') {
      processNeighbouringCells(processingCell);
    }
  };

  const startProcessing = () => {
    [...cellsToProcess].forEach(([rowId, colsMap]) => {
      [...colsMap].forEach(([colId]) => {
        const currentCell = board[rowId][colId];
        if (isCellVisited(currentCell)) {
          return;
        }
        processCell(currentCell);

        const rowMap = cellsToProcess.get(rowId);
        if (rowMap) {
          rowMap.delete(colId);
          if (rowMap.size === 0) {
            cellsToProcess.delete(rowId);
          }
        }
      });
    });
  };

  addToMap(cellsToProcess, cell);

  while (cellsToProcess.size > 0) {
    startProcessing();
  }

  return board;
};
/*
- cell
- add to process
- go through processing cells
- for each cell
  - visit cell
  - visible = true
  - if ''
    - get neighbour cells
    -
*/
